import { Info } from 'shared/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import ErrorCard from '../../ui/ErrorCard';
import Select from '../../ui/Select';
import { SkeletonTable } from '../../ui/Skeleton';
import StandardTable from '../../ui/StandardTable';
import { GetSla } from './inventoryApi';

/**
 * SLA por operador, máquina e tipo de OS (M9, item 3).
 *
 * ⚠️ NENHUM CONTADOR. Todo número desta tela sai de `accepted_at`,
 * `finished_at` e das pausas que a execução JÁ grava — a mesma função pura do
 * M5 (`ComputeServiceOrderSla`), com as pausas descontadas.
 *
 * E OS CANCELADA NÃO É CRONOMETRADA: ela aparece contada numa coluna própria,
 * nunca na média. Essa decisão veio do console do M5 ("o tempo dela nunca
 * aparece sob tempo de execução") e é repetida aqui porque a tela é outra.
 */

const DIMENSIONS = ['TYPE', 'OPERATOR', 'EQUIPMENT'];

const FormatInt = (value) => new Intl.NumberFormat('pt-BR').format(Number(value ?? 0));

/** Segundos → "1h 04min" / "12min 30s" / "45s". Traço quando não há medida. */
function FormatDuration(seconds) {
    if (seconds === null || seconds === undefined) {
        return '—';
    }
    const total = Math.max(0, Math.round(Number(seconds)));
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const rest = total % 60;
    if (hours > 0) {
        return `${hours}h ${String(minutes).padStart(2, '0')}min`;
    }
    if (minutes > 0) {
        return `${minutes}min ${String(rest).padStart(2, '0')}s`;
    }
    return `${rest}s`;
}

const InventorySlaPanel = ({ label }) => {
    const [dimension, setDimension] = useState('TYPE');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            setData(await GetSla({ dimension }));
        } catch (error) {
            setLoadError(error?.message || 'Erro ao carregar');
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [dimension]);

    useEffect(() => {
        load();
    }, [load]);

    const columns = useMemo(
        () => [
            {
                key: 'label',
                label: label(`wms.sla.dimension.${dimension}`),
                getValue: (row) => row.label || row.code || '',
                render: (row) => (
                    <span className={row.key ? '' : 'italic text-mono-500'}>
                        {row.label || row.code || label('wms.sla.no_group')}
                    </span>
                ),
            },
            {
                key: 'orders_count',
                label: label('wms.sla.orders'),
                getValue: (row) => row.orders_count,
                render: (row) => FormatInt(row.orders_count),
            },
            {
                key: 'measured_count',
                label: label('wms.sla.measured'),
                getValue: (row) => row.measured_count,
                render: (row) => FormatInt(row.measured_count),
            },
            {
                key: 'execution_seconds_avg',
                label: label('wms.sla.execution_avg'),
                getValue: (row) => row.execution_seconds_avg ?? -1,
                render: (row) => <span className="font-semibold">{FormatDuration(row.execution_seconds_avg)}</span>,
            },
            {
                key: 'execution_seconds_p50',
                label: label('wms.sla.execution_p50'),
                getValue: (row) => row.execution_seconds_p50 ?? -1,
                render: (row) => FormatDuration(row.execution_seconds_p50),
            },
            {
                key: 'execution_seconds_p90',
                label: label('wms.sla.execution_p90'),
                getValue: (row) => row.execution_seconds_p90 ?? -1,
                render: (row) => FormatDuration(row.execution_seconds_p90),
            },
            {
                key: 'execution_seconds_max',
                label: label('wms.sla.execution_max'),
                getValue: (row) => row.execution_seconds_max ?? -1,
                render: (row) => FormatDuration(row.execution_seconds_max),
            },
            {
                key: 'paused_seconds',
                label: label('wms.sla.paused'),
                getValue: (row) => row.paused_seconds,
                render: (row) => FormatDuration(row.paused_seconds),
            },
            {
                key: 'canceled_count',
                label: label('wms.sla.canceled'),
                getValue: (row) => row.canceled_count,
                render: (row) => FormatInt(row.canceled_count),
            },
            {
                key: 'items_skipped_count',
                label: label('wms.sla.skipped_items'),
                getValue: (row) => row.items_skipped_count,
                render: (row) =>
                    row.items_skipped_count > 0 ? (
                        <span className="font-semibold text-warning-text dark:text-warning-300">
                            {FormatInt(row.items_skipped_count)}
                        </span>
                    ) : (
                        FormatInt(0)
                    ),
            },
        ],
        [dimension, label],
    );

    if (loading && !data) {
        return <SkeletonTable columns={10} rows={5} />;
    }

    if (loadError) {
        return (
            <ErrorCard
                title={label('wms.sla.title')}
                message={loadError}
                action={label('wms.common.retry')}
                actionFn={load}
            />
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex items-start gap-3 rounded-xl border-2 border-info-400 bg-info-soft px-4 py-3 dark:border-info-700 dark:bg-info-900/30">
                <Info size={22} className="mt-0.5 shrink-0 text-info-text dark:text-info-300" />
                <p className="text-sm text-info-text dark:text-info-200">{label('wms.sla.pauses_discounted')}</p>
            </div>

            <StandardTable
                rows={data?.rows ?? []}
                columns={columns}
                rowKey={(row, index) => row.key || `none-${index}`}
                countLabel={label('wms.sla.title')}
                exportFileName={`wms-sla-${dimension.toLowerCase()}`}
                emptyMessage={label('wms.sla.empty')}
                searchEnabled={false}
                toolbarLeft={
                    <Select value={dimension} onChange={(event) => setDimension(event.target.value)}>
                        {DIMENSIONS.map((value) => (
                            <option key={value} value={value}>
                                {label(`wms.sla.dimension.${value}`)}
                            </option>
                        ))}
                    </Select>
                }
            />
        </div>
    );
};

export default InventorySlaPanel;
